import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  buildGalleryListOptions,
  buildGallerySearchParams,
  GALLERY_SEARCH_DEBOUNCE_MS,
  GALLERY_SEARCH_MIN_LENGTH,
  parseGalleryPageFromSearchParams,
  type GallerySearchState,
} from '../galleryListParams';
import type { GalleryPageSize } from '../../../types/userConfig';

export function useGalleryListSearchParams(pageSize: GalleryPageSize) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDebounceTimeoutRef = useRef<number | null>(null);

  const urlSearchValue = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearchValue);
  const [previousUrlSearchValue, setPreviousUrlSearchValue] =
    useState(urlSearchValue);

  if (urlSearchValue !== previousUrlSearchValue) {
    setPreviousUrlSearchValue(urlSearchValue);
    setSearchInput(urlSearchValue);
  }

  const galleryPage = useMemo(
    () => parseGalleryPageFromSearchParams(searchParams),
    [searchParams],
  );

  const gallerySearch = useMemo((): GallerySearchState => {
    const trimmed = urlSearchValue.trim();
    return {
      debouncedQuery:
        trimmed.length >= GALLERY_SEARCH_MIN_LENGTH ? trimmed : '',
    };
  }, [urlSearchValue]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  const replaceParams = useCallback(
    ({
      page,
      searchInputValue,
    }: {
      page: number;
      searchInputValue: string;
    }) => {
      setSearchParams(
        buildGallerySearchParams({
          page,
          searchInput: searchInputValue,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);

      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
        searchDebounceTimeoutRef.current = null;
      }

      if (value.trim() === '') {
        replaceParams({
          page: 1,
          searchInputValue: '',
        });
        return;
      }

      searchDebounceTimeoutRef.current = window.setTimeout(() => {
        replaceParams({
          page: 1,
          searchInputValue: value,
        });
        searchDebounceTimeoutRef.current = null;
      }, GALLERY_SEARCH_DEBOUNCE_MS);
    },
    [replaceParams],
  );

  const setGalleryPage = useCallback(
    (page: number) => {
      replaceParams({
        page,
        searchInputValue: searchInput,
      });
    },
    [replaceParams, searchInput],
  );

  const listOptions = useMemo(
    () => buildGalleryListOptions(gallerySearch, galleryPage, pageSize),
    [galleryPage, gallerySearch, pageSize],
  );

  const hasActiveGallerySearch =
    gallerySearch.debouncedQuery.length >= GALLERY_SEARCH_MIN_LENGTH;

  return {
    galleryPage,
    gallerySearch,
    searchInput,
    listOptions,
    hasActiveGallerySearch,
    handleSearchChange,
    setGalleryPage,
  };
}
